import React, { useEffect, useState } from "react";
import { Check, X, Sparkles, ArrowRight, Lock, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * VitalityVaultBanner — shown on Dashboard for users without an active plan.
 * Walks them through the 3-step qualifier wizard. When all 3 are done,
 * "Unlock trial" lights up. Activates a 7-day full-feature trial.
 */
export default function VitalityVaultBanner({ subscription, onTrialStarted }) {
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    // Only check eligibility for users without a plan
    if (subscription && subscription.status === "active") return;
    api.trialEligibility().then(setElig).catch(() => {});
  }, [subscription]);

  // Active trial — show a countdown badge
  if (subscription?.is_trial) {
    const exp = new Date(subscription.trial_expires_at);
    const daysLeft = Math.max(0, Math.ceil((exp - new Date()) / 86400000));
    return (
      <div className="border-2 border-[#F59E0B] bg-[#FFFBEB] p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="trial-active-badge">
        <div className="flex items-center gap-3">
          <Flame size={20} className="text-[#B45309]"/>
          <div>
            <div className="font-display font-bold text-lg">Vitality Vault Trial · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</div>
            <div className="text-xs text-[#52525B] mt-1">Full Operator-tier access. Subscribe before {exp.toLocaleDateString()} to keep your work.</div>
          </div>
        </div>
        <button
          onClick={() => navigate("/pricing")}
          data-testid="trial-upgrade-button"
          className="bg-black text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]"
        >
          Lock in a plan <ArrowRight size={14}/>
        </button>
      </div>
    );
  }

  if (subscription?.status === "active") return null;
  if (!elig) return null;

  // Already used trial → show paywall
  if (elig.used) {
    return (
      <div className="border-2 border-[#0A0A0B] p-6 mb-6" data-testid="paywall-banner">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={18}/>
          <div className="text-eyebrow">Subscription required</div>
        </div>
        <h3 className="font-display font-black tracking-tighter text-3xl mb-2">Your trial ended. Pick a tier to keep going.</h3>
        <p className="text-sm text-[#52525B] mb-4 max-w-2xl">Brands, knowledge base, interview answers, scheduled posts — all preserved. Subscribe and pick up exactly where you left off.</p>
        <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]" data-testid="paywall-upgrade-button">
          See pricing <ArrowRight size={14}/>
        </button>
      </div>
    );
  }

  // Not yet eligible — show the wizard
  const done = elig.checklist.filter((c) => c.done).length;
  const total = elig.checklist.length;

  const start = async () => {
    setWorking(true);
    try {
      await api.startTrial();
      toast.success("Trial activated · 7 days of full access");
      onTrialStarted && onTrialStarted();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Could not start trial");
    } finally { setWorking(false); }
  };

  return (
    <div className="border-2 border-black p-6 mb-6 bg-white" data-testid="vault-wizard-banner">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-eyebrow mb-2 flex items-center gap-2"><Sparkles size={12}/> Vitality Vault · 7-day full-feature trial</div>
          <h3 className="font-display font-black tracking-tighter text-3xl">Complete setup → unlock the engine for 7 days, free.</h3>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">No credit card. We just want to be sure you'll actually use it. Three setup steps prove you're serious.</p>
        </div>
        <div className="mono text-eyebrow">{done}/{total} done</div>
      </div>

      <div className="space-y-2 mb-5">
        {elig.checklist.map((c) => (
          <div key={c.key} data-testid={`vault-step-${c.key}`} className={`flex items-center justify-between p-3 border ${c.done ? "border-[#059669] bg-[#ECFDF5]" : "border-[#E2E4E9] hover:border-black"}`}>
            <div className="flex items-center gap-3">
              {c.done ? (
                <div className="w-5 h-5 bg-[#059669] flex items-center justify-center"><Check size={12} className="text-white"/></div>
              ) : (
                <div className="w-5 h-5 border-2 border-[#A1A1AA]"></div>
              )}
              <span className={`text-sm font-medium ${c.done ? "" : ""}`}>{c.label}</span>
            </div>
            {!c.done && (
              <button
                onClick={() => navigate(c.key === "brand" ? "/brands" : c.key === "interview" ? "/brands" : "/brands")}
                className="text-eyebrow hover:text-black"
                data-testid={`vault-step-${c.key}-go`}
              >
                Go →
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={start}
          disabled={!elig.eligible || working}
          data-testid="start-trial-button"
          className={`px-6 py-3 text-sm flex items-center gap-2 transition-colors ${
            elig.eligible ? "bg-black text-white hover:bg-[#F59E0B] hover:text-black" : "bg-[#F4F5F6] text-[#A1A1AA] cursor-not-allowed"
          }`}
        >
          <Sparkles size={14}/> {elig.eligible ? "Unlock 7-day trial" : "Finish steps to unlock"}
        </button>
        <button onClick={() => navigate("/pricing")} className="text-eyebrow hover:text-black" data-testid="skip-trial-button">
          Or subscribe straight away →
        </button>
      </div>
    </div>
  );
}
